import { createContext, useContext, useMemo, useState, ReactNode, useEffect, useCallback } from 'react'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { platformNameFromChannel, SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { providerFromChannelType } from 'components/ArticlePostPreview/utils'
import {
  PostableChannelType,
  providerFromDesignChannelType,
  providerNameFromChannelType,
  PostOptionsPage,
} from './Types'
import { getPlatformValidators } from 'components/ArticlePostPreview/validators'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useCompleteDesignAction } from 'components/Design/utils/useCompleteDesignAction'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import dayjs, { Dayjs } from 'dayjs'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { DesignActionType } from 'types'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { useQueryClient } from 'react-query'
import { scheduledPostsKeys } from 'hooks/queries/scheduledPosts/scheduledPostsKeys'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { updateCalendarItemInCache } from 'hooks/queries/calendar/utils'

export interface PostPreviewContextProps {
  page: PostOptionsPage
  setPage: (page: PostOptionsPage) => void
  scheduledPost: ScheduledPostShow | undefined
  isLoading: boolean
  provider: ExternalIntegrationEnum | undefined
  providerName: string | undefined
  hasIntegration: boolean
  validationErrors: string[]
  dateTime: Dayjs | undefined
  setDateTime: (date: Dayjs | undefined) => void
  handlePublish: () => Promise<void>
  handleSchedule: (date: Date, value: PostStatusPropertyValue) => Promise<void>
  handleSetReminder: (date: Date, value: PostStatusPropertyValue) => Promise<void>
  isUpdating: boolean
  previewIsStale: boolean
  isPostable: boolean
  isPosting: boolean
  channel: DesignChannel | PostableChannelType
  status: PostStatusPropertyValue | undefined
  fileId: string
}

export const PostPreviewContext = createContext<PostPreviewContextProps | null>(null)

export const usePostPreviewContext = () => {
  const context = useContext(PostPreviewContext)
  if (!context) {
    throw new Error('usePostPreviewContext must be used within a PostPreviewProvider')
  }
  return context
}

interface PostPreviewProviderProps {
  children: ReactNode
  initialPage?: PostOptionsPage
  fileId: string
  scheduledPostId: number
  channel: DesignChannel | PostableChannelType
}

export const PostPreviewProvider = ({
  children,
  initialPage,
  fileId,
  scheduledPostId,
  channel,
}: PostPreviewProviderProps) => {
  const [page, setPage] = useState<PostOptionsPage>(initialPage || PostOptionsPage.PostDetails)
  const [isUpdating, setIsUpdating] = useState(false)
  const { data: scheduledPost, isLoading, refetch } = useScheduledPost({ id: scheduledPostId }, { staleTime: 0 })
  const integrationsQuery = useExternalIntegrations()
  const publishScheduledPost = usePublishScheduledPost()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  const setPageWithRefetch = useCallback(
    (newPage: PostOptionsPage) => {
      setPage(newPage)

      if (newPage === PostOptionsPage.Schedule) {
        refetch()
      }
    },
    [refetch],
  )

  const isDesignChannel = (Object.values(DesignChannel) as string[]).includes(channel)
  const provider = isDesignChannel
    ? providerFromDesignChannelType[channel as DesignChannel]
    : providerFromChannelType[channel as SocialChannelType]

  const providerName = !!isDesignChannel
    ? providerNameFromChannelType[channel as DesignChannel]
    : platformNameFromChannel[channel as SocialChannelType]

  const hasIntegration = !!integrationsQuery.data?.external_integrations.some((i) => i.provider === provider)

  const validators = provider ? getPlatformValidators(provider, channel) : null

  const isMediaProcessing = useMemo(() => {
    if (!scheduledPost || !isDesignChannel) return false

    const mediaObjects = scheduledPost.media_objects || []

    if (mediaObjects.length === 0) return true

    return mediaObjects.some((obj) => {
      if (!obj) return false

      const isVideo = obj.mime_type?.includes('video')
      if (isVideo && typeof obj.duration === 'undefined') return true
      if (!isVideo && (!obj.width || !obj.height)) return true
      return false
    })
  }, [scheduledPost, isDesignChannel])

  const validationErrors = useMemo(() => {
    if (!validators || !scheduledPost) return []

    if (isDesignChannel && isMediaProcessing) {
      return []
    }

    // For TikTok, check if creator info is still loading to prevent validation errors during initialization
    const isTikTokChannel = channel === DesignChannel.TikTokPost
    const isTikTokLoading = isTikTokChannel && !scheduledPost.post_settings

    if (isTikTokLoading) {
      return []
    }

    return [
      ...validators.validateFiles(scheduledPost.media_objects),
      ...validators.validateContent(scheduledPost.content || ''),
      ...validators.validateVideosSize(scheduledPost.media_objects),
      ...validators.validateImagesSize(scheduledPost.media_objects),
      ...validators.validateVideosDuration(scheduledPost.media_objects),
      ...validators.validateVideosAspectRatio(scheduledPost.media_objects),
      ...validators.validateVideosFormat(scheduledPost.media_objects),
      ...validators.validateVideosCount(scheduledPost.media_objects),
      ...(validators.validateVideosWidth ? validators.validateVideosWidth(scheduledPost.media_objects) : []),
      ...(validators.validateVideosFrameRate ? validators.validateVideosFrameRate(scheduledPost.media_objects) : []),
      ...validators.validatePostSettings(scheduledPost.post_settings),
    ]
  }, [validators, scheduledPost, isDesignChannel, isMediaProcessing, channel])

  const completeActionMutation = useCompleteDesignAction()
  const { updatePostAt, updateStatus, postAt, status } = usePostProperties(
    {
      id: fileId,
      type: AssigneeType.Design,
    },
    scheduledPost?.almanac_file.parent_id,
  )

  const [dateTime, setDateTime] = useState<Dayjs | undefined>(() => {
    const date = dayjs(postAt || scheduledPost?.post_at)
    return date.isValid() ? date : dayjs().add(7, 'days').startOf('hour')
  })

  useEffect(() => {
    const date = dayjs(postAt || scheduledPost?.post_at)
    setDateTime(date.isValid() ? date : dayjs().add(7, 'days').startOf('hour'))
  }, [postAt, scheduledPost?.post_at])

  const previewIsStale =
    scheduledPost &&
    scheduledPost.almanac_file.type === 'design' &&
    scheduledPost.almanac_file.version &&
    scheduledPost.almanac_file.last_rendered_version !== undefined &&
    scheduledPost.almanac_file.last_rendered_version < scheduledPost.almanac_file.version

  useEffect(() => {
    if (!isMediaProcessing && !previewIsStale) return

    const interval = setInterval(() => {
      refetch()
    }, 1000)

    return () => clearInterval(interval)
  }, [isMediaProcessing, previewIsStale, refetch])

  const handlePublish = async () => {
    if (!scheduledPost) return

    await updatePostAt(dayjs().toISOString())
    await publishScheduledPost.mutateAsync({ id: scheduledPost.id })
    if (scheduledPost.source_type === 'Design') completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
  }

  const handleSchedule = async (date: Date, value: PostStatusPropertyValue) => {
    if (!scheduledPost) return

    setIsUpdating(true)
    Promise.all([updatePostAt(date.toISOString()), updateStatus(value)])
      .then(() => {
        setPage(PostOptionsPage.PostDetails)
        const formattedDate = dayjs(dateTime).format('ddd, MMM DD, h:mm A')
        showToastMessage(`Post Scheduled For ${formattedDate}`, 'success')

        const itemKey = scheduledPost.almanac_file.key || scheduledPost.almanac_file.id
        updateCalendarItemInCache(
          queryClient,
          itemKey,
          (item) => ({
            ...item,
            post_at: date.toISOString(),
            status: value,
          }),
          workspaceId,
        )

        queryClient.invalidateQueries(scheduledPostsKeys.index(workspaceId))

        if (scheduledPost.source_type === 'Design') {
          completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
        }
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }

  const handleSetReminder = async (date: Date, value: PostStatusPropertyValue) => {
    if (!scheduledPost) return

    setIsUpdating(true)
    Promise.all([updatePostAt(date.toISOString()), updateStatus(value)])
      .then(() => {
        setPage(PostOptionsPage.PostDetails)
        const formattedDate = dayjs(dateTime).format('ddd, MMM DD, h:mm A')
        showToastMessage(`Post Scheduled For ${formattedDate}`, 'success')
        if (scheduledPost.source_type === 'Design') {
          completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
        }
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }

  const isPosting = publishScheduledPost.isLoading
  const isPostable = !isLoading && !isUpdating && !isPosting && !validationErrors.length && !previewIsStale

  useEffect(() => {
    if (!postAt) return
    setDateTime(dayjs(postAt))
  }, [postAt])

  const value: PostPreviewContextProps = {
    page,
    setPage: setPageWithRefetch,
    scheduledPost,
    isLoading: isLoading || integrationsQuery.isLoading,
    provider,
    providerName,
    hasIntegration,
    validationErrors,
    dateTime,
    setDateTime,
    handlePublish,
    handleSchedule,
    handleSetReminder,
    isUpdating: isUpdating || isPosting,
    isPosting,
    previewIsStale: !!previewIsStale,
    isPostable,
    channel,
    status: status?.value,
    fileId,
  }

  return <PostPreviewContext.Provider value={value}>{children}</PostPreviewContext.Provider>
}
